import { Field, Form, Formik } from "formik";
import { useGetAccounts } from "hooks/useAccounts";
import useSchema from "hooks/useSchema";
import { isEmpty } from "lodash";
import Button from "pages/_components/Button";
import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import { ResponsiveHeader } from "pages/_components/header";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { actions as notificationActions } from "reducers/notification";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as i18n from "util/i18n";
import { amountTypes, formatPreparedData } from "util/servicePayments";
import * as Yup from "yup";
import { bool } from "prop-types";
import { useDynamicForm, usePaymentModes } from "../NewServicePayment/hooks";

const FORM_ID = "payments.service";

const DetailPayPage = ({ isDesktop }) => {
    const { hash } = useParams();
    const dispatch = useDispatch();
    const { numberRequired, stringRequired } = useSchema();
    const { handleDynamicValues } = useDynamicForm();
    const { fetchingModes, isCABpayment } = usePaymentModes(handleDynamicValues);
    const placeholder = i18n.get("global.select");
    const { fetching, debts } = useSelector((state) => state.servicePayments);

    const { accountOptions, fetching: fetchingAccounts, accounts, formatLabel } = useGetAccounts("ARS", "", "", true);
    const scheduledBill = debts.find((debt) => debt.hash === hash);

    const initialValues = {
        debitAccountId: "",
        amountField: {
            amount: scheduledBill?.amount1,
            currency: "ARS",
        },
    };

    function validateAmount(value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { debitAccountId = "" } = this.parent;
        const selected = accounts.find((_account) => _account.productId === debitAccountId);

        if (value.amount < scheduledBill?.billingThresholds[0]?.minAmount1) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("payments.service.min.amount.error", null, {
                        minAmount: scheduledBill?.billingThresholds[0]?.minAmount1,
                    }),
                    "error",
                    ["newServicePaymentPage"],
                ),
            );
        }
        if (value.amount > scheduledBill?.billingThresholds[0]?.maxAmount1) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("payments.service.max.amount.error", null, {
                        maxAmount: scheduledBill?.billingThresholds[0]?.maxAmount1,
                    }),
                    "error",
                    ["newServicePaymentPage"],
                ),
            );
        }
        if (value.amount > selected?.availableBalance) {
            dispatch(
                notificationActions.showNotification(i18n.get("transfers.insuficient.amount.error"), "error", [
                    "newServicePaymentPage",
                ]),
            );
        }
        return (
            value.amount <= selected?.availableBalance ||
            value.amount >= scheduledBill?.billingThresholds[0]?.minAmount1 ||
            value.amount <= scheduledBill?.billingThresholds[0]?.maxAmount1
        );
    }

    const validationSchema = Yup.object().shape({
        debitAccountId: stringRequired,
        amountField: Yup.object()
            .shape({
                amount: numberRequired,
            })
            .test("availableAmount", i18n.get("transfer.amount.error"), validateAmount),
    });

    const handleSubmit = (values) => {
        const accountLabel = formatLabel(
            accounts.find(({ productId }) => productId === values.debitAccountId),
            true,
        );

        const params = {
            billToPay: { ...scheduledBill, ...scheduledBill?.billingThresholds[0] },
            values: { ...values, companyCode: scheduledBill?.companyCode, paymentType: scheduledBill?.paymentModeId },
            accountLabel,
            isCABpayment,
            companyName: scheduledBill?.companyName,
            form: scheduledBill?.searchForm,
        };

        const { prepareData } = formatPreparedData(params);

        gaEventTracker.triggerEvent(
            events.pagos_pagar_enviar({ empresa: prepareData.companyName, modalidadPago: prepareData.paymentMethod }),
        );

        dispatch(servicePaymentsActions.preparePayment({ ...prepareData, fromScheduledServices: true }));
    };

    useEffect(() => {
        if (debts?.length === 0) {
            dispatch(servicePaymentsActions.getDebts());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!isEmpty(scheduledBill)) {
            dispatch(servicePaymentsActions.getPaymentModes(scheduledBill?.companyCode));
        }
    }, [dispatch, scheduledBill]);

    return (
        <>
            <Notification scopeToShow="newServicePaymentPage" />
            <ResponsiveHeader id="payments.service.new" />
            <MainContainer
                showLoader={fetching || fetchingAccounts || fetchingModes}
                className="service-payments__detail">
                <GridTable className="payment-details-list">
                    <GridTable.Container className={isDesktop && "service-payments-flex-container"}>
                        <GridTable.Data>
                            <CustomData
                                titleText="payments.service.companyCode.label"
                                titleTextType="I18N"
                                valueText={scheduledBill?.companyName}
                            />
                        </GridTable.Data>
                        {scheduledBill?.details.map((detail) => (
                            <GridTable.Data key={`summary-bill-${detail.label}`}>
                                <CustomData titleText={detail.label} valueText={detail.value} />
                            </GridTable.Data>
                        ))}
                        <GridTable.Data>
                            <CustomData
                                titleText="payments.service.paymentId"
                                titleTextType="I18N"
                                valueText={scheduledBill?.serviceId}
                            />
                        </GridTable.Data>
                    </GridTable.Container>
                </GridTable>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}>
                    {({ values }) => {
                        const canSubmit =
                            !values.debitAccountId || !values.amountField?.amount || isEmpty(scheduledBill);
                        const isClosedAmount = scheduledBill?.billingThresholds[0]?.amountType === amountTypes.ESC;
                        const disableAmountField = !values.debitAccountId || isClosedAmount;
                        return (
                            <>
                                <Form>
                                    <GridTable className="form">
                                        <GridTable.Container columns={12} columnsTemplate="fixed">
                                            <GridTable.Data columnStart={2} columnWidth={10}>
                                                <Field
                                                    component={ProductSelector}
                                                    label={`${i18n.get(`${FORM_ID}.debitAccountId.label`)}*`}
                                                    name="debitAccountId"
                                                    placeholder={placeholder}
                                                    data={{ emptyOptionLabel: "", options: accountOptions }}
                                                />
                                            </GridTable.Data>
                                        </GridTable.Container>

                                        <GridTable.Container columns={12} columnsTemplate="fixed">
                                            <GridTable.Data columnStart={2} columnWidth={5}>
                                                <Field
                                                    component={AmountField}
                                                    idForm={FORM_ID}
                                                    name="amountField"
                                                    labelText={`${i18n.get("payments.service.totalAmount")}*`}
                                                    data={{
                                                        options: [{ id: 1, label: i18n.get(`currency.label.ARS`) }],
                                                    }}
                                                    disableNumber={disableAmountField}
                                                    clearable={false}
                                                    maxLength={16}
                                                    disable={isClosedAmount}
                                                    disableSelect
                                                    fixedDecimalScale
                                                />
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                    <Row className="form-actions">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="payments.service.pay"
                                                loading={fetching}
                                                disabled={canSubmit}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

DetailPayPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default DetailPayPage;
